/**
 * Dual price display.
 *
 * The shop is priced in EUR. Shopify Markets converts prices to the visitor's
 * market currency before Liquid ever renders them, so `{{ price | money }}`
 * already reads e.g. "$73.08" for a US shopper. Store policy is that the euro
 * price stays the headline number, with the amount actually charged shown
 * underneath — so this flips the two: it recovers the EUR base from
 * `Shopify.currency.rate` (the same rate Shopify used to convert) and promotes
 * it, keeping the converted amount as the secondary line.
 *
 * Markup contract — produced by snippets/seed-price.liquid:
 *   <span class="seed-price" data-seed-price="7308">$73.08</span>
 * Eurozone visitors keep exactly that; only non-EUR markets are rewritten.
 *
 * Public API, for scripts that repaint a price (variant pickers, cart):
 *   SeedDualPrice.isDual                  — whether a second line applies
 *   SeedDualPrice.set(node, cents)        — repaint a [data-seed-price] node
 *   SeedDualPrice.formatShop(cents)       — cents (shop currency) → string
 */
window.SeedDualPrice = (function () {
  var config = window.SeedShop || {};
  var currency = (window.Shopify && window.Shopify.currency) || {};
  var shopFormat = config.moneyFormat || '{{amount}}';
  var label = config.localPriceLabel || '';

  var rate = parseFloat(currency.rate);
  var isDual = Boolean(
    config.currency && currency.active && currency.active !== config.currency && rate && isFinite(rate) && rate !== 1
  );

  /** Renders cents using the shop's own money format (i.e. euros). */
  function formatShop(cents) {
    var withDot = (cents / 100).toFixed(2);
    var withComma = withDot.replace('.', ',');
    var rounded = String(Math.round(cents / 100));

    return shopFormat
      .replace(/\{\{\s*amount\s*\}\}/g, withDot)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, rounded)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, rounded)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, withDot);
  }

  /** Renders cents in the visitor's market currency. */
  function formatLocal(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency.active,
      }).format(cents / 100);
    } catch (error) {
      return formatShop(cents);
    }
  }

  /**
   * Paints a price node. `cents` is in the presentment currency — the same
   * scale Liquid and the variants JSON use.
   */
  function set(node, cents, localText) {
    if (!node || !isFinite(cents)) return;
    node.dataset.seedPrice = String(cents);

    if (!isDual) {
      node.textContent = formatShop(cents);
      return;
    }

    // Supporting amounts (compare-at, savings) show the shop currency only, so
    // they read against the headline rather than stacking a second pair.
    if (node.dataset.seedPriceMode === 'base') {
      node.textContent = formatShop(Math.round(cents / rate));
      return;
    }

    // Shopify computed presentment = round(base * rate); dividing back and
    // rounding to the cent recovers the exact base for realistic rates.
    var base = document.createElement('span');
    base.className = 'seed-price__base';
    base.textContent = formatShop(Math.round(cents / rate));

    var local = document.createElement('span');
    local.className = 'seed-price__local';
    var localValue = localText || formatLocal(cents);
    local.textContent = label ? label + ' ' + localValue : localValue;

    node.textContent = '';
    node.appendChild(base);
    node.appendChild(local);
    node.classList.add('seed-price--dual');
  }

  function convert(node) {
    if (node.dataset.seedPriceDone) return;
    var cents = parseInt(node.dataset.seedPrice, 10);
    if (!isFinite(cents)) return;
    node.dataset.seedPriceDone = 'true';
    // Liquid already rendered the converted amount in Shopify's exact format —
    // better than re-deriving it, so pass it through as the local line.
    set(node, cents, node.textContent.trim());
  }

  function scan(root) {
    if (!isDual) return;
    var scope = root || document;
    if (scope.nodeType === 1 && scope.matches('[data-seed-price]')) convert(scope);
    scope.querySelectorAll('[data-seed-price]').forEach(convert);
  }

  if (isDual) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        scan();
      });
    } else {
      scan();
    }

    // Prices arrive later from the Section Rendering API (recommendations,
    // cart drawer), so keep watching rather than scanning once.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  return { isDual: isDual, rate: rate, set: set, scan: scan, formatShop: formatShop, formatLocal: formatLocal };
})();
